import { useEffect, useRef, useState } from 'react';
import { useOfferPageContext } from 'pages/offer/context';
import { DatePicker } from 'shared/ui';
import { ActionButtonType } from '../types';
import { Typography, TypographyType, TypographyColor, TypographyFontWeight } from './typography';
import { Spacing, SpacingDirection, SpacingSize } from './spacing';
import { ModalBackground } from './modal-background';
import styles from './employee-decision.module.scss';
import { ActionButton } from './action-button';
import { ButtonVariant } from './button';

/**
 * Block with new employee offer decision
 */
export const EmployeeDecision = () => {
  const [divHeight, setDivHeight] = useState<number | null>(null);
  const divRef = useRef<HTMLDivElement>(null);

  const {
    userData: { recruiterEmail, recruiterName, recruiterTelephone, actionEnds },
    accept,
    reject,
    step,
    isModalOpened,
    isErrorDate,
    closeModal,
    selectedDate,
    selectDate,
    confirmReject,
    formattedSelectedDate,
    plan,
  } = useOfferPageContext();

  useEffect(() => {
    if (divRef.current) {
      setDivHeight(divRef.current.offsetHeight);
    }
  }, []);

  return (
    <ModalBackground isModalOpened={isModalOpened} setModalOpened={closeModal} replaceBlockHeight={`${divHeight}px`}>
      <div ref={divRef} className={styles.employeeDecision}>
        <Spacing size={SpacingSize.medium} direction={SpacingDirection.vertical} className={styles.leftSideBlock}>
          <Typography type={TypographyType.h2}>Решение по офферу</Typography>
          {step === 'initial' && (
            <>
              <Spacing direction={SpacingDirection.both} size={SpacingSize.small} className={styles.buttonsBlock}>
                <ActionButton onClick={accept} action={ActionButtonType.accept} variant={ButtonVariant.primaryV2} />
                <ActionButton
                  onClick={reject}
                  action={ActionButtonType.decline}
                  variant={ButtonVariant.secondaryV2}
                  className={styles.declineButton}
                />
              </Spacing>

              <Typography type={TypographyType.h5}>Срок действия предложения: {actionEnds}</Typography>
            </>
          )}

          {step === 'chooseDate' && (
            <>
              <Typography type={TypographyType.h5}>Выберите дату первого рабочего дня</Typography>

              <Spacing
                direction={SpacingDirection.vertical}
                size={SpacingSize.medium}
                className={styles.buttonsAndDatePicker}
              >
                <DatePicker
                  selectedDate={selectedDate}
                  selectDate={selectDate}
                  placeholder="Дата выхода"
                  isError={isErrorDate}
                />

                <Spacing direction={SpacingDirection.both} size={SpacingSize.small} className={styles.buttonsBlock}>
                  <ActionButton
                    onClick={plan}
                    action={ActionButtonType.accept}
                    variant={ButtonVariant.primaryV2}
                    className={styles.chooseDateAccept}
                    text="Запланировать"
                  />
                  <ActionButton
                    onClick={closeModal}
                    action={ActionButtonType.decline}
                    variant={ButtonVariant.secondaryV2}
                    className={styles.chooseDateAcceptDecline}
                    text="Отменить"
                  />
                </Spacing>
              </Spacing>
            </>
          )}

          {step === 'accepted' && (
            <Typography className={styles.scheduledText} type={TypographyType.h5}>
              Дата первого рабочего дня: <span>{formattedSelectedDate}</span>
            </Typography>
          )}

          {step === 'reject' && (
            <>
              <Typography type={TypographyType.h5}>Вы уверены, что хотите отказаться?</Typography>

              <Spacing direction={SpacingDirection.both} size={SpacingSize.small} className={styles.buttonsBlock}>
                <ActionButton
                  onClick={confirmReject}
                  action={ActionButtonType.accept}
                  variant={ButtonVariant.primaryV2}
                  className={styles.cancelledAccept}
                  text="Да"
                />
                <ActionButton
                  onClick={closeModal}
                  action={ActionButtonType.decline}
                  variant={ButtonVariant.secondaryV2}
                  className={styles.chooseDateAcceptDecline}
                  text="Отменить"
                />
              </Spacing>
            </>
          )}

          {step === 'rejected' && (
            <Typography className={styles.rejectedText} type={TypographyType.h5}>
              Нам жаль, что вы не стали частью нашей команды.
            </Typography>
          )}
        </Spacing>

        <Spacing size={SpacingSize.medium} direction={SpacingDirection.vertical} className={styles.rightSideBlock}>
          <Typography weight={TypographyFontWeight.regular} type={TypographyType.h5} color={TypographyColor.white}>
            По всем вопросам ты можешь связаться со своим рекрутером:
          </Typography>

          <Typography type={TypographyType.h5} color={TypographyColor.white} className={styles.contacts}>
            {recruiterName}
          </Typography>

          {recruiterEmail && (
            <Typography type={TypographyType.h5} color={TypographyColor.white} className={styles.contacts}>
              <a href={`tel:+${recruiterTelephone}`}>{recruiterTelephone}</a>
              <br />
              <a target="_blank" href={`mailto:${recruiterEmail}`} rel="noreferrer">
                Почта: {recruiterEmail}
              </a>
            </Typography>
          )}
        </Spacing>
      </div>
    </ModalBackground>
  );
};
